import { replaceDecimalCode } from 'oxygen-lib-ui/util/string';
import { BASE_RENDER_OPS } from 'components/Image';
import { imageUrl } from 'util/images';
import { parse as parseJson } from 'util/json';

const RENDER_OPS = {
  thumbnail: {
    ...BASE_RENDER_OPS,
    width: 768
  },
  'og:image': {
    ...BASE_RENDER_OPS,
    width: 1500,
    aspectRatio: '3:2'
  },
  'twitter:image': {
    ...BASE_RENDER_OPS,
    width: 800,
    height: 400
  },
  googleStructuredData: ['16:9', '4:3', '1:1'].map(aspectRatio => ({
    ...BASE_RENDER_OPS,
    width: 1500,
    format: 'jpg',
    aspectRatio
  })),
  'flipboard-image': {
    ...BASE_RENDER_OPS,
    width: 1500,
    format: 'jpg'
  }
};

/**
 * custom component mapper for metadata bundle items.
 *
 * look at the item's variant to map our metadata bundle items. we want to
 * keep the graphql schema sane (w/r/t union types), so we're having all
 * metadata items use the 'metadata' item type. that means we need to store the
 * _kind_ of metadata it is on the variant.
 *
 * the arguments conform to signature for customComponentMapper prop/callback
 * in BundleItemComponent.
 *
 * @param {Object} item bundle item
 * @param {String} _variant bundle variant (unused)
 * @param {Object} components component map key is variant, value is component
 * @param {Number} _idx current index of bundle item in array
 * @param {Number} _totalItems total length of bundle item array
 * @param {Function} DEFAULT_COMPONENT default component to use if no component exists for variant
 * @returns {Function} component
 */
function customComponentMapper(
  item,
  _variant,
  components,
  _idx,
  _totalItems,
  DEFAULT_COMPONENT
) {
  const { variant } = item;

  const mappedComponent = components[variant]
    ? components[variant]
    : DEFAULT_COMPONENT;

  return mappedComponent;
}

/**
 * find strings to be output to DOM and run them through a html entities cleaner
 *
 * @export
 * @param {Object} obj multidimensional object which contains an object 'data' that contains an object with the key 'value'
 * @param {String} obj.variant
 * @param {Object} obj.data
 * @param {String} obj.data.value
 * @returns {Object} entire multidimensional object passed with cleaned string in the data object
 */
function replaceEntitiesInData(obj) {
  if (
    !['scripts', 'links'].includes(obj.variant) &&
    Object.keys(obj).includes('data')
  ) {
    const cleanData = { data: {} };

    Object.keys(obj.data).map(prop =>
      Object.assign(cleanData.data, {
        [prop]:
          prop === 'value' ? replaceDecimalCode(obj.data[prop]) : obj.data[prop]
      })
    );

    return Object.assign({}, obj, cleanData);
  }

  return obj;
}

/**
 * updates image URLs embedded in JSON-LD data. currently handles image.url and
 * thumbnailUrl.
 *
 * @param {Object} jsonLd JSON-LD data
 * @param {Object} jsonLd.image
 * @param {String} jsonLd.image.url image URL
 * @param {String} jsonLd.thumbnailUrl thumbnail image URL
 * @returns {Object} updated JSON-LD data
 */
function updateImageUrlsInJsonLd(jsonLd) {
  return {
    ...jsonLd,
    ...{
      // update image object
      ...(jsonLd.image &&
        jsonLd.image.url && {
          image: RENDER_OPS.googleStructuredData.map(opts => ({
            ...jsonLd.image,
            url: imageUrl(jsonLd.image.url, opts)
          }))
        }),
      // update thumbnailUrl key
      ...(jsonLd.thumbnailUrl && {
        thumbnailUrl: imageUrl(jsonLd.thumbnailUrl, RENDER_OPS.thumbnail)
      })
    }
  };
}

/**
 * updates imageUrls in key page bundle metadata items for two purposes:
 *
 * - so that we don't expose direct storage URLs to the world
 * - to optimize each image instance for specific sizing/rendering
 *
 * @param {Object} item a single page bundle metadata item
 * @param {String} item.variant
 * @param {Object} item.data
 * @param {String} item.data.name
 * @param {String} item.data.value
 * @returns {Object} copy of item, with updates
 */
function processImageUrls(item) {
  // variant / data.name
  const metadataPaths = {
    default: 'thumbnail',
    opengraph: 'og:image',
    twitter: 'twitter:image',
    scripts: 'application/ld+json',
    flipboard: 'flipboard-image'
  };

  const {
    variant,
    data: { name, value }
  } = item;

  // bail early if we don't need to process the items for image URLs
  if (metadataPaths[variant] !== name) {
    return item;
  }

  const updatedValue =
    name === metadataPaths.scripts
      ? JSON.stringify(updateImageUrlsInJsonLd(parseJson(value, {})))
      : imageUrl(value, RENDER_OPS[name] || {});

  return {
    ...item,
    data: {
      name,
      value: updatedValue
    }
  };
}

function fixJsonLdDateValues(jsonLd) {
  const { dateUpdated, dateRepromoted, ...rest } = jsonLd;

  return {
    ...rest,
    // override dateModified with dateUpdated if present
    ...(dateUpdated && { dateModified: dateUpdated })
  };
}

/**
 * removes invalid properties from the JSON-LD and overrides dateModified with
 * dateUpdated, if present.
 *
 * @param {Object} item
 * @param {Object} item.data
 * @param {String} item.data.name
 * @param {String} item.data.value
 */
function processJsonLd(item) {
  const {
    data: { name, value }
  } = item;

  return {
    ...item,
    data: {
      name,
      value: JSON.stringify(fixJsonLdDateValues(parseJson(value, {})))
    }
  };
}

export {
  customComponentMapper,
  replaceDecimalCode,
  replaceEntitiesInData,
  processImageUrls,
  updateImageUrlsInJsonLd,
  processJsonLd,
  fixJsonLdDateValues
};
